import merge from 'lodash/merge';
import { useBundleContext } from '../store';
import { ThemeApplication } from '../../../components/BundleSettings/Theming/api';

export function transformThemeApplicationToChakraTheme(
  theme: ThemeApplication,
) {
  return {
    colors: getColors(theme),
    semanticTokens: getSemanticTokens(theme),
    components: getComponents(theme),
    styles: {
      global: {
        body: {
          '--awtomic-content-width':
            theme.settingsOverride.globals.width || '100%',
        },
      },
    },
  };
}

export const getColors = (theme) => {
  const { settingsOverride } = theme;
  const transformedColorStyles = Object.keys(settingsOverride.componentStyles)
    .map((component) => {
      switch (component) {
        case 'Stepper': {
          return getStepperColors(settingsOverride.componentStyles[component]);
        }
        case 'ProgressBar': {
          return getProgressBarColors(
            settingsOverride.componentStyles[component],
          );
        }
      }
    })
    .filter(Boolean);

  const colors = transformedColorStyles.reduce(
    (acc, colors) => merge(acc, colors),
    {},
  );

  return colors;
};

export const parseSellingPlanStyles = (sellingPlanSettings) => {
  if (!sellingPlanSettings) return {};

  const {
    textColor,
    backgroundColor,
    hoverTextColor,
    hoverBgColor,
    selectedTextColor,
    selectedBgColor,
    borderColor,
    borderRadius,
    borderWidth,
    containerBgColor,
    labelColor,
    headingColor,
    headingFontSize,
    headingFontWeight,
    buttonPadding,
    buttonMinWidth,
    buttonMinHeight,
    buttonFontSize,
    buttonFontWeight,
  } = sellingPlanSettings;

  return {
    Container: {
      backgroundColor: containerBgColor,
    },
    Heading: {
      color: headingColor,
      fontSize: headingFontSize,
      fontWeight: headingFontWeight,
    },
    Label: {
      color: labelColor,
    },
    SellingPlanSelector: {
      '& label': {
        color: textColor,
        backgroundColor: backgroundColor,
        border:
          borderWidth >= 1 ? `${borderWidth}px solid ${borderColor}` : 'none',
        borderRadius: borderRadius,
        padding: buttonPadding,
        minWidth: buttonMinWidth,
        minHeight: buttonMinHeight,
        fontSize: buttonFontSize,
        fontWeight: buttonFontWeight,
      },
      '& label:hover': {
        color: hoverTextColor,
        backgroundColor: hoverBgColor,
      },
      '& label[data-selected="true"]': {
        color: selectedTextColor,
        backgroundColor: selectedBgColor,
      },
    },
  };
};

export const getSemanticTokens = (theme) => {
  const { settingsOverride } = theme;
  const primaryColor = settingsOverride.globals.colors.primary;
  const secondaryColor = settingsOverride.globals.colors.secondary;
  const backgroundColor = settingsOverride.globals.colors.backgroundColor;

  return {
    colors: {
      primary: {
        default: primaryColor,
      },
      secondary: {
        default: secondaryColor,
      },
      bg: {
        default: backgroundColor || '#ffffff',
        light: backgroundColor || '#ffffff',
        dark: backgroundColor || '#111111',
      },
    },
  };
};

const getButtonStyleDefinitions = (theme) => {
  const { settingsOverride } = theme;
  const buttonVariants =
    settingsOverride.componentStyles?.Button?.variants || {};

  return {
    variants: Object.keys(buttonVariants)?.reduce(
      (acc, variant) => ({
        ...acc,
        [variant]: {
          color:
            settingsOverride.componentStyles.Button.variants[variant].color,
          backgroundColor:
            settingsOverride.componentStyles.Button.variants[variant].bgColor,
          borderRadius:
            settingsOverride.componentStyles.Button.variants[variant]
              .borderRadius,
          border:
            settingsOverride.componentStyles.Button.variants[variant].border,
          ['&:hover']: {
            color:
              settingsOverride.componentStyles.Button.variants[variant]
                .hoverColor,
            backgroundColor:
              settingsOverride.componentStyles.Button.variants[variant]
                .hoverBgColor,
          },
        },
      }),
      {},
    ),
  };
};

export const getComponents = (theme) => {
  return {
    Button: getButtonStyleDefinitions(theme),
  };
};

const getStepperColors = (stepperSettings) => {
  const { stepLabels, completeBg, completeText, incompleteBg, incompleteText } =
    stepperSettings;

  return {
    stepper: {
      stepCounter: {
        background: {
          complete: completeBg,
          incomplete: incompleteBg,
        },
        color: {
          complete: completeText,
          incomplete: incompleteText,
        },
      },
      label: {
        color: stepLabels,
      },
    },
  };
};

const getProgressBarColors = (progressBarSettings) => {
  if (!progressBarSettings) return {};

  const {
    progressBarComplete,
    progressBarIncomplete,
    labelComplete,
    labelIncomplete,
    isSticky,
    stickyBottom,
    stickyZIndex,
    stickyBoxShadow,
    backgroundColor,
    boxShadow,
  } = progressBarSettings;

  return {
    progressBar: {
      progressBarItem: {
        background: {
          complete: progressBarComplete,
          incomplete: progressBarIncomplete,
        },
      },
      productCountLabel: {
        color: {
          incomplete: labelIncomplete,
          complete: labelComplete,
        },
      },
      isSticky,
      stickyBottom,
      stickyZIndex,
      stickyBoxShadow,
      backgroundColor,
      boxShadow,
    },
  };
};

export const parseFiltersStyles = (filterSettings) => {
  const {
    labelColor,
    titleColor,
    optionColor,
    optionCheckBoxColor,
    resetButtonColor,
    resetButtonBgColor,
    dropdownBg,
    dropDownBorderColor,
    dropdownBorderWidth,
    showDropdownBorder,
    dropdownBorderRadius,
  } = filterSettings;

  return {
    filterLabel: {
      color: labelColor,
    },
    filterTitle: {
      color: titleColor,
    },
    filterOption: {
      color: optionColor,
      display: 'flex',
      alignItems: 'baseline',
      '.chakra-checkbox__control': {
        borderColor: optionCheckBoxColor,
      },
    },
    filterResetButton: {
      color: resetButtonColor,
      backgroundColor: resetButtonBgColor,
    },
    filterMenuDropdown: {
      background: dropdownBg,
      borderColor: dropDownBorderColor,
      borderWidth: dropdownBorderWidth,
      borderStyle: showDropdownBorder ? 'solid' : 'none',
      borderRadius: dropdownBorderRadius,
    },
  };
};

export const parseProductSelectorStyles = (productSelectorSettings) => {
  const {
    textColor,
    backgroundColor,
    hoverTextColor,
    hoverBgColor,
    selectedTextColor,
    selectedBgColor,
    borderColor,
    borderRadius,
    borderWidth,
    containerBgColor,
  } = productSelectorSettings;

  return {
    Container: {
      backgroundColor: containerBgColor,
    },
    ProductSelector: {
      '& label': {
        color: textColor,
        backgroundColor: backgroundColor,
        border:
          borderWidth >= 1 ? `${borderWidth}px solid ${borderColor}` : 'none',
        borderRadius: borderRadius,
      },
      '& label:hover': {
        color: hoverTextColor,
        backgroundColor: hoverBgColor,
      },
      '& label[data-selected="true"]': {
        color: selectedTextColor,
        backgroundColor: selectedBgColor,
      },
    },
  };
};

export const parseStepperStyles = (stepperSettings) => {
  const { stepLabels, completeBg, completeText, incompleteBg, incompleteText } =
    stepperSettings;

  return {
    stepItemLabel: {
      color: stepLabels,
    },
    stepCounter: {
      background: incompleteBg,
      color: incompleteText,
      [`&[data-current-step="true"]`]: {
        background: completeBg,
        color: completeText,
      },
    },
    checkMark: {
      fill: completeText,
    },
  };
};

export const parseFooterStyles = (footerSettings) => {
  const {
    backgroundColor,
    textColor,
    primaryButtonBg,
    primaryButtonText,
    primaryButtonHoverBg,
    secondaryButtonBg,
    secondaryButtonText,
    secondaryButtonHoverBg,
    borderRadius,
    borderColor,
    borderWidth,
    emphasisTextColor,
    strikethroughTextColor,
    layoutMode = 'absolute',
    position,
    width,
    maxWidth,
    margin,
    padding,
    zIndex,
    gridArea,
    flexBasis,
    flexGrow,
    flexShrink,
    alignSelf,
    justifySelf,
  } = footerSettings || {};

  const getPositionStyles = () => {
    switch (layoutMode) {
      case 'absolute':
        return {
          position: 'absolute',
          left: '0',
          bottom: '0',
          width: width || '100%',
          zIndex: zIndex || 10,
        };
      case 'relative':
        return {
          position: 'relative',
          width: width || '100%',
          margin: margin || '0',
        };
      case 'grid':
        return {
          position: 'relative',
          gridArea: gridArea || 'auto',
          justifySelf: justifySelf || 'stretch',
          alignSelf: alignSelf || 'end',
          width: width || '100%',
        };
      case 'flex':
        return {
          position: 'relative',
          flexBasis: flexBasis || '100%',
          flexGrow: flexGrow || 0,
          flexShrink: flexShrink || 0,
          alignSelf: alignSelf || 'stretch',
          width: width || '100%',
        };
      default:
        return {
          position: position || 'absolute',
          left: '0',
          bottom: '0',
          width: width || '100%',
        };
    }
  };

  const positionStyles = getPositionStyles();

  return {
    CreateBundleFooter: {
      ...positionStyles,
      backgroundColor: backgroundColor || 'bg.default',
      borderTop: borderWidth
        ? `${borderWidth}px solid ${borderColor}`
        : undefined,
      color: textColor,
      padding: padding || '1rem',
      maxWidth: maxWidth,
      minHeight: '80px',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      '&[data-is-preview="true"]': {
        position: 'static',
      },
    },
    CreateBundleButtonInfo: {
      color: textColor,
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '2rem',
      '& span[data-modifier="emphasis"]': {
        color: emphasisTextColor,
      },
      '& span[data-modifier="strikethrough"]': {
        color: strikethroughTextColor,
        textDecoration: 'line-through',
      },
    },
    '& button[variant="primary"]': {
      backgroundColor: primaryButtonBg,
      color: primaryButtonText,
      borderRadius,
      '&:hover:not([disabled])': {
        backgroundColor: primaryButtonHoverBg,
      },
    },
    '& button[variant="secondary"]': {
      backgroundColor: secondaryButtonBg,
      color: secondaryButtonText,
      borderRadius,
      '&:hover:not([disabled])': {
        backgroundColor: secondaryButtonHoverBg,
      },
    },
  };
};

export const parseSectionsStyles = (sectionsSettings) => {
  if (!sectionsSettings) return {};

  const {
    layoutMode = 'grid',
    responsive,
    cardsPerRow,
    cardWidth,
    cardMinWidth = '330px',
    cardMaxWidth = '1fr',
    cardGap = '16px',
    justifyItems = 'center',
  } = sectionsSettings;
  const {
    mobileCards = 1,
    tabletCards = 2,
    desktopCards = 3,
    largeDesktopCards = 4,
  } = generateResponsivePresets(cardsPerRow);

  const getGridTemplateColumns = () => {
    if (responsive) {
      return {
        base: `repeat(${mobileCards}, minmax(0, 1fr))`,
        sm: `repeat(${mobileCards}, minmax(0, 1fr))`,
        md: `repeat(${tabletCards}, minmax(0, 1fr))`,
        lg: `repeat(${desktopCards}, minmax(0, 1fr))`,
        '2xl': `repeat(${largeDesktopCards}, minmax(0, 1fr))`,
      };
    }

    if (cardsPerRow) {
      return `repeat(${cardsPerRow}, minmax(0, 1fr))`;
    }

    // If explicit card width is specified
    if (cardWidth) {
      return `repeat(auto-fill, minmax(${cardWidth}, ${cardMaxWidth}))`;
    }

    // Default responsive behavior - auto-fit
    return `repeat(auto-fit, minmax(${cardMinWidth}, ${cardMaxWidth}))`;
  };

  return {
    layoutMode,
    responsive,
    container: {
      display: layoutMode,
      width: '100%',
      justifyItems: justifyItems,
      gridTemplateColumns: getGridTemplateColumns(),
      gap: cardGap,
    },
    card: {},
  };
};

export const parseProductCardStyles = (cardSettings) => {
  if (!cardSettings) return {};

  const {
    // Card general properties
    cardWidth = '220px',
    height = '100%',
    padding = '16px',
    margin = '0 auto',
    textAlign = 'center',
    border,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    background,
    color,
    boxShadow,
    transition,

    // Hover properties
    hoverBackground,
    hoverBorderColor,
    hoverColor,
    hoverBoxShadow,

    // Layout
    layoutDirection = 'column',
    layoutImagePosition = 'top',
    layoutContentAlign = 'start',
    layoutSpacing = '12px',
    layoutContentSpacing = '8px',

    // Image properties
    imageWidth,
    imageHeight,
    imageBorderRadius = '8px',
    imageOverflow = 'hidden',
    imageBackgroundTransparency = false,
    imageBackground,

    // Badge properties
    showBadgeWhenSelected = true,
    badgeText,
    badgePosition = 'top-right',
    badgeBackgroundColor = '#000000',
    badgeTextColor = '#ffffff',
    badgeFontSize = '12px',
    badgeFontWeight = '600',
    badgePadding = '4px 8px',
    badgeMargin = '8px',
    badgeBorderRadius = '2px',
    badgeZIndex = 2,

    // Title properties
    titleOrder = 0,
    titleFontSize,
    titleFontWeight,
    titleColor,
    titleAlign,
    titleMargin,

    // Price properties
    priceOrder = 1,
    priceFontSize = '16px',
    priceFontWeight = '600',
    priceColor = 'rgb(99, 115, 129)',
    priceAlign = 'center',
    priceMargin = '0',

    // Description properties
    descriptionOrder = 2,
    descriptionFontSize,
    descriptionColor,
    descriptionMargin,

    // Button properties
    buttonOrder = 3,
    buttonBackground,
    buttonColor,
    buttonBorderRadius,
    buttonPadding,
    buttonFontSize,
    buttonFontWeight,
    buttonBorder,
    buttonHoverBackground,
    buttonHoverColor,

    // Quantity container properties
    quantityContainerJustifyContent = 'space-between',
    quantityContainerAlignItems = 'center',
    quantityContainerWidth = '140px',
    quantityContainerHeight,
    quantityContainerMargin = '0 auto',
    quantityContainerPadding = '12px',
    quantityContainerBackground = '#f6f6f6',
    quantityContainerHoverBackground = '#f6f6f6',
    quantityContainerBorder = '1px solid #c4cdd5',
    quantityContainerBorderRadius = '22px',
    quantityContainerBoxShadow,
    quantityContainerOrder,
    quantityContainerAvailableBackground = 'transparent',
    quantityContainerAvailableColor = 'currentColor',
    showAddButtonForZeroQuantity = true,
    addButtonText,
    addButtonTextColor,
    addButtonBackgroundColor,
    addButtonFontSize,
    addButtonFontWeight = '500',
    // Quantity buttons properties
    quantityButtonAlignItems = 'center',
    quantityButtonJustifyContent = 'center',
    quantityButtonBackground = 'none',
    quantityButtonColor,
    quantityButtonBorder = 'none',
    quantityButtonBorderRadius,
    quantityButtonPadding,
    quantityButtonMinWidth,
    quantityButtonMinHeight = 'initial',
    quantityButtonHeight = 'initial',
    quantityButtonCursor = 'pointer',
    quantityButtonTransition = 'all 0.2s ease',
    quantityButtonHoverBackground = 'initial',
    quantityButtonHoverColor,
    quantityButtonDisabledVisibility = 'hidden',
    quantityButtonDisabledOpacity,
    quantityButtonDisabledCursor = 'not-allowed',

    // Quantity text properties
    quantityTextFontSize = '14px',
    quantityTextFontWeight = '400',
    quantityTextColor,
    quantityTextPadding,
    quantityTextMargin,
    quantityTextAlign = 'center',
    quantityTextLineHeight,
    quantityTextDisabledOpacity = '0.3',

    // Icon properties
    minusIconWidth = '16px',
    minusIconHeight = '16px',
    minusIconTransition = 'all 0.2s ease',
    plusIconWidth = '16px',
    plusIconHeight = '16px',
    plusIconTransition = 'all 0.2s ease',
  } = cardSettings;

  // Convert badge position string to CSS properties
  const getBadgePositionStyles = () => {
    switch (badgePosition) {
      case 'top-right':
        return { top: badgeMargin, right: badgeMargin };
      case 'top-left':
        return { top: badgeMargin, left: badgeMargin };
      case 'bottom-right':
        return { bottom: badgeMargin, right: badgeMargin };
      case 'bottom-left':
        return { bottom: badgeMargin, left: badgeMargin };
      default:
        return { top: badgeMargin, right: badgeMargin };
    }
  };

  const badgePositionStyles = getBadgePositionStyles();

  return {
    CardContainer: {
      width: cardWidth,
      height,
      padding,
      margin,
      border,
      borderColor,
      borderWidth,
      borderStyle,
      borderRadius,
      background,
      color,
      boxShadow,
      transition,
      textAlign,
      display: 'flex',
      flexDirection: layoutDirection,
      alignItems: layoutContentAlign,
      gap: layoutSpacing,
      position: 'relative',
      _hover: {
        background: hoverBackground,
        borderColor: hoverBorderColor,
        color: hoverColor,
        boxShadow: hoverBoxShadow,
      },
    },
    imageContainer: {
      order:
        layoutImagePosition === 'top'
          ? 0
          : layoutImagePosition === 'bottom'
            ? 3
            : layoutImagePosition === 'left' && layoutDirection === 'row'
              ? 0
              : 1,
      width: imageWidth,
      height: imageHeight,
      borderRadius: imageBorderRadius,
      overflow: imageOverflow,
      position: 'relative',

      '.bundle-lazy-image-container': {
        backgroundColor: imageBackground,
      },
      '.bundle-lazy-image-container img': {
        ...(imageBackgroundTransparency && {
          mixBlendMode: 'multiply',
        }),
      },
    },
    Badge: {
      position: 'absolute',
      ...badgePositionStyles,
      backgroundColor: badgeBackgroundColor,
      color: badgeTextColor,
      fontSize: badgeFontSize,
      fontWeight: badgeFontWeight,
      padding: badgePadding,
      borderRadius: badgeBorderRadius,
      zIndex: badgeZIndex,
      textTransform: 'uppercase',
    },
    showBadgeWhenSelected,
    badgeText,
    content: {
      order:
        layoutImagePosition === 'top'
          ? 1
          : layoutImagePosition === 'bottom'
            ? 0
            : layoutImagePosition === 'left' && layoutDirection === 'row'
              ? 1
              : 0,
      display: 'flex',
      flexDirection: 'column',
      gap: layoutContentSpacing,
      flex: 1,
    },
    CardTitle: {
      order: titleOrder,
      fontSize: titleFontSize,
      fontWeight: titleFontWeight,
      color: titleColor,
      textAlign: titleAlign,
      margin: titleMargin,
    },
    CardPrice: {
      order: priceOrder,
      textAlign: priceAlign,
      margin: priceMargin,
      span: {
        fontSize: priceFontSize,
        fontWeight: priceFontWeight,
        color: priceColor,
      },
    },
    description: {
      order: descriptionOrder,
      fontSize: descriptionFontSize,
      color: descriptionColor,
      margin: descriptionMargin,
    },
    button: {
      order: buttonOrder,
      background: buttonBackground,
      color: buttonColor,
      borderRadius: buttonBorderRadius,
      padding: buttonPadding,
      fontSize: buttonFontSize,
      fontWeight: buttonFontWeight,
      border: buttonBorder,
      _hover: {
        background: buttonHoverBackground,
        color: buttonHoverColor,
      },
    },
    QuantityButtonContainer: {
      display: 'flex',
      justifyContent: quantityContainerJustifyContent,
      alignItems: quantityContainerAlignItems,
      width: quantityContainerWidth,
      height: quantityContainerHeight,
      margin: quantityContainerMargin,
      padding: quantityContainerPadding,
      background: quantityContainerAvailableBackground,
      border: quantityContainerBorder,
      borderRadius: quantityContainerBorderRadius,
      boxShadow: quantityContainerBoxShadow,
      order: quantityContainerOrder,
      '&:hover': {
        background: quantityContainerHoverBackground,
      },
      '&[data-available="true"]': {
        background: quantityContainerBackground,
      },
      '&[data-available="false"]': {
        span: {
          color: quantityContainerAvailableColor,
        },
      },
    },
    QuantityButton: {
      display: 'flex',
      alignItems: quantityButtonAlignItems,
      justifyContent: quantityButtonJustifyContent,
      background: quantityButtonBackground,
      color: quantityButtonColor,
      border: quantityButtonBorder,
      borderRadius: quantityButtonBorderRadius,
      padding: quantityButtonPadding,
      minWidth: quantityButtonMinWidth,
      minHeight: quantityButtonMinHeight,
      height: quantityButtonHeight,
      cursor: quantityButtonCursor,
      transition: quantityButtonTransition,
      '&:hover': {
        backgroundColor: quantityButtonHoverBackground,
        color: quantityButtonHoverColor,
      },
      '&:disabled': {
        visibility: quantityButtonDisabledVisibility,
        opacity: quantityButtonDisabledOpacity,
        cursor: quantityButtonDisabledCursor,
      },
    },
    Quantity: {
      fontSize: quantityTextFontSize,
      fontWeight: quantityTextFontWeight,
      color: quantityTextColor,
      padding: quantityTextPadding,
      margin: quantityTextMargin,
      textAlign: quantityTextAlign,
      lineHeight: quantityTextLineHeight,
      ':disabled': {
        opacity: quantityTextDisabledOpacity,
      },
    },
    AddButton: {
      color: addButtonTextColor || quantityTextColor,
      fontSize: addButtonFontSize || quantityTextFontSize,
      fontWeight: addButtonFontWeight,
      backgroundColor: addButtonBackgroundColor || 'transparent',
      cursor: 'pointer',
      display: 'inline-block',
      width: '100%',
    },
    showAddButtonForZeroQuantity,
    addButtonText,
    CustomMinusIcon: {
      width: minusIconWidth,
      height: minusIconHeight,
      color: quantityTextColor,
      fill: quantityTextColor,
      transition: minusIconTransition,
    },
    CustomPlusIcon: {
      width: plusIconWidth,
      height: plusIconHeight,
      color: quantityTextColor,
      fill: quantityTextColor,
      transition: plusIconTransition,
    },
  };
};

export const useIdStyling = (id) => {
  const styles = useBundleContext(
    (state) => state.settingsOverride.componentStyles,
  );

  return styles[id] || {};
};

function generateResponsivePresets(cardsPerRow: any): {
  mobileCards?: number;
  tabletCards?: number;
  desktopCards?: number;
  largeDesktopCards?: number;
} {
  // Default values if cardsPerRow is not provided
  if (!cardsPerRow || typeof cardsPerRow !== 'number') {
    return {
      mobileCards: 1,
      tabletCards: 2,
      desktopCards: 3,
      largeDesktopCards: 4,
    };
  }

  // For small screens, show at least 1 card
  const mobileCards = Math.max(1, Math.floor(cardsPerRow / 3));

  // For tablet screens, show about 2/3 of desktop cards
  const tabletCards = Math.max(2, Math.floor(cardsPerRow * 0.67));

  // Desktop uses the exact number specified
  const desktopCards = cardsPerRow;

  // For large screens, show the exact number of cards
  // TODO: see this have sense in the future
  const largeDesktopCards = desktopCards;

  return {
    mobileCards,
    tabletCards,
    desktopCards,
    largeDesktopCards,
  };
}

export const parseHeroSectionStyles = (heroSectionSettings) => {
  if (!heroSectionSettings) return {};

  const {
    // Container properties
    minHeight,
    width,
    maxWidth,
    padding,
    margin,
    backgroundColor,
    backgroundImage,
    backgroundOverlay,
    backgroundOverlayOpacity,
    border,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,

    // Title properties
    titleFontSize,
    titleFontWeight,
    titleColor,
    titleLineHeight,
    titleLetterSpacing,
    titleTextAlign,
    titleTextTransform,
    titleMargin,
    titleTextShadow,
    titleMaxWidth,

    // Subtitle properties
    subtitleFontSize,
    subtitleFontWeight,
    subtitleColor,
    subtitleLineHeight,
    subtitleLetterSpacing,
    subtitleTextAlign,
    subtitleTextTransform,
    subtitleMargin,
    subtitleTextShadow,
    subtitleMaxWidth,
    subtitleOpacity,

    // Layout properties
    contentAlign,
    contentJustify,
    contentMaxWidth,
    contentPadding,

    // Animation properties
    animationEffect,
    transitionDuration,
  } = heroSectionSettings;

  const getOverlayColor = () => {
    if (!backgroundOverlay) return 'rgba(0, 0, 0, 0.4)';

    // If a hex color is provided, convert it to rgba with the given opacity
    if (backgroundOverlay.startsWith('#')) {
      const opacity =
        backgroundOverlayOpacity !== undefined ? backgroundOverlayOpacity : 0.4;
      const hex = backgroundOverlay.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    return backgroundOverlay;
  };

  return {
    Container: {
      minHeight,
      width,
      maxWidth,
      padding,
      margin,
      backgroundColor,
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
      border:
        border ||
        (borderWidth && borderColor
          ? `${borderWidth}px ${borderStyle || 'solid'} ${borderColor}`
          : undefined),
      borderRadius,
      justifyContent: contentJustify,
      alignItems: contentAlign,
      textAlign:
        contentAlign === 'center'
          ? 'center'
          : contentAlign === 'flex-start'
            ? 'left'
            : contentAlign === 'flex-end'
              ? 'right'
              : undefined,
      transition: transitionDuration
        ? `all ${transitionDuration}s ease`
        : undefined,
      animationEffect,
      '&::before': backgroundImage
        ? {
            backgroundColor: getOverlayColor(),
            opacity:
              backgroundOverlayOpacity !== undefined
                ? backgroundOverlayOpacity
                : 0.4,
          }
        : { display: 'none' },
    },
    Title: {
      fontSize: titleFontSize,
      fontWeight: titleFontWeight,
      color: titleColor,
      lineHeight: titleLineHeight,
      letterSpacing: titleLetterSpacing,
      textAlign: titleTextAlign,
      textTransform: titleTextTransform,
      margin: titleMargin,
      textShadow: titleTextShadow,
      maxWidth: titleMaxWidth,
    },
    SubTitle: {
      fontSize: subtitleFontSize,
      fontWeight: subtitleFontWeight,
      color: subtitleColor,
      lineHeight: subtitleLineHeight,
      letterSpacing: subtitleLetterSpacing,
      textAlign: subtitleTextAlign,
      textTransform: subtitleTextTransform,
      margin: subtitleMargin,
      textShadow: subtitleTextShadow,
      maxWidth: subtitleMaxWidth,
      opacity: subtitleOpacity,
    },
    Content: {
      maxWidth: contentMaxWidth,
      padding: contentPadding,
    },
  };
};

export const parseProgressBarOverrides = (progressBarSettings) => {
  if (!progressBarSettings) return {};

  const {
    // Container properties
    width,
    maxWidth,
    position,
    backgroundColor,
    borderTop,
    borderColor,
    borderWidth,
    borderStyle,
    zIndex,
    padding,
    margin,
    boxShadow,

    // Item properties
    itemHeight = '36px',
    itemWidth = '100%',
    progressBarComplete,
    progressBarIncomplete,

    // Label properties
    labelComplete,
    labelIncomplete,
    labelFontSize,
    labelLineHeight,

    // Remove button properties
    removeButtonDisplay,
    removeButtonSymbol,

    // Sticky mode properties
    isSticky,
    stickyBottom = '0',
    stickyZIndex = 11,
    stickyBoxShadow = '0 -2px 10px rgba(0, 0, 0, 0.1)',
  } = progressBarSettings;

  const getBorderStyle = () => {
    if (borderTop) return borderTop;
    if (borderWidth && borderColor)
      return `${borderWidth}px ${borderStyle || 'solid'} ${borderColor}`;
    return undefined;
  };

  return {
    ProgressBarContainer: {
      width,
      maxWidth,
      position: isSticky ? 'sticky' : position,
      bottom: isSticky ? stickyBottom : undefined,
      zIndex: isSticky ? stickyZIndex : zIndex,
      backgroundColor,
      borderTop: getBorderStyle(),
      padding,
      margin,
      boxShadow,
      ...(isSticky && {
        position: 'sticky',
        bottom: stickyBottom,
        zIndex: stickyZIndex,
        backgroundColor: backgroundColor || 'white',
        borderTop: getBorderStyle() || '1px solid #ccc',
        boxShadow: boxShadow || stickyBoxShadow,
      }),
    },
    ProgressBarItem: {
      height: itemHeight,
      width: itemWidth,
      backgroundColor:
        progressBarIncomplete ||
        'progressBar.progressBarItem.background.incomplete',
      '&[data-selected="true"]': {
        backgroundColor:
          progressBarComplete ||
          'progressBar.progressBarItem.background.complete',
      },
    },
    ProductCountLabelContainer: {
      color:
        labelIncomplete || 'progressBar.productCountLabel.color.incomplete',
      '&[data-is-empty="true"]': {
        color: labelComplete || 'progressBar.productCountLabel.color.complete',
      },
    },
    ProgressBarCountLabel: {
      fontSize: labelFontSize || '14px',
      lineHeight: labelLineHeight || '16px',
    },
    RemoveButton: {
      display: removeButtonDisplay || 'none',
      ...(removeButtonSymbol && {
        '&::after': {
          content: `"${removeButtonSymbol}"`,
        },
      }),
    },
  };
};
